import { createContext, useContext } from 'react';

import type { StaffProfile, StaffRole } from '@/api/types';

/**
 * The auth context, split out from the provider so the provider file exports
 * only a component — Vite's fast refresh gives up on a module that mixes the
 * two, and a dispatcher losing filter state on every save is a real cost.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthState {
  status: AuthStatus;
  /** Non-null exactly when status is 'authenticated'. */
  staff: StaffProfile | null;
  signIn: (email: string, password: string) => Promise<StaffProfile>;
  signOut: () => Promise<void>;
  /** Re-reads /staff/auth/me — call after anything that could change the row. */
  reload: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return value;
}

/**
 * The signed-in staff member, or a throw.
 *
 * For screens that only ever render behind a RequireRole: it removes the null
 * check that would otherwise be repeated in every admin page, without a `!`.
 */
export function useStaff(): StaffProfile {
  const { staff } = useAuth();
  if (staff === null) {
    throw new Error('useStaff was called outside an authenticated route');
  }
  return staff;
}

/** Where a signed-in person belongs. The root redirect and RequireRole share it. */
export function homePathForRole(role: StaffRole): string {
  return role === 'admin' ? '/admin' : '/driver';
}
